import { CommonModule } from '@angular/common'
import { Component, computed, input, OnInit, signal, ViewEncapsulation } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatChipsModule } from '@angular/material/chips'
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Subject, takeUntil } from 'rxjs'
import {
  CreateWordPairDicArgs,
  CreateWordPairDicCommand,
} from 'src/app/core/commands/create-word-pair-dic/create-word-pair-dic.command'
import { AngularMaterialsModules } from 'src/app/module/materials.module'
import { CategoryService } from 'src/app/service/data/category.service'
import { DictionaryService } from 'src/app/service/data/dictionary.service'
import { TagService } from 'src/app/service/data/tag.service'
import { ThemeService } from 'src/app/service/theme/theme.service'
import { UserService } from 'src/app/service/user-info/user.service'
import { Article, ArticlesByLanguage, Languages, PartOfSpeech } from '../../enums/enums'
import { SelectOption } from '../../interfaces/word'
import { DdcButtonSOneComponent } from '../ddc-button-s-one/ddc-button-s-one.component'
import { DdcElemChooserOneComponent } from '../ddc-elem-chooser-one/ddc-elem-chooser-one.component'
import { DdcTextGradientComponent } from '../ddc-text-gradient/ddc-text-gradient.component'

@Component({
  selector: 'ddc-add-mobile-sheet',
  templateUrl: './ddc-add-mobile-sheet.component.html',
  styleUrls: ['./ddc-add-mobile-sheet.component.scss'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  providers: [CreateWordPairDicCommand],
  imports: [
    AngularMaterialsModules,
    CommonModule,
    MatChipsModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatDialogModule,
    DdcButtonSOneComponent,
    DdcElemChooserOneComponent,
    DdcTextGradientComponent,
  ],
})
export class DdcAddMobileSheetComponent implements OnInit {
  target_dic = input<{ id: number; title: string }>(undefined)

  protected CreateWordPairDicCommand: CreateWordPairDicCommand
  _form: FormGroup

  private formValues = signal<any>(null)
  private selectedTags = signal<{ id: number; label: string }[]>([])
  private selectedTags2 = signal<{ id: number; label: string }[]>([])
  private selectedCategory = signal<{ id: number; label: string }[]>([])
  private selectedCategory2 = signal<{ id: number; label: string }[]>([])
  protected selectedDics = signal<{ id: number; label: string }[]>([])

  private commandArgs = computed(() => {
    const value = this.formValues()
    if (!value) return null

    const args: CreateWordPairDicArgs = {
      word1: {
        language: value._1_language,
        article: value._1_article,
        word: value._1_word,
        pos: value._1_pos,
        definition: value._1_definition,
        example: value._1_example,
        category: this.selectedCategory()?.length > 0 ? this.selectedCategory()[0] : undefined,
        tags: this.selectedTags(),
      },
      word2: undefined,
      privacy: value._privacy,
      dictionaries: this.selectedDics(),
    }

    // Add word2 data if it's a pair
    if (this._selectedChipOptions.includes('pair')) {
      args.word2 = {
        language: value._2_language,
        article: value._2_article,
        word: value._2_word,
        pos: value._2_pos,
        definition: value._2_definition,
        example: value._2_example,
        category: this.selectedCategory2()?.length > 0 ? this.selectedCategory2()[0] : undefined,
        tags: this.selectedTags2(),
      }
    }

    // Add dictionary data if creating a dictionary
    if (this._selectedChipOptions.includes('dictionary')) {
      args.dictionary_title = value._dictionary_title
      args.dictionary_description = value._dictionary_description
    }

    return args
  })
  _isDialogLoading = false
  _isDarkTheme = false
  _isLightTheme = true
  _isPaperTheme = false
  _wordCreated = false
  _pairCreated = false
  _hasDic = false
  _targetDicTitle = ''
  _addError = ''
  _isSubscriptionError = false
  _addedField = ''
  _addedField2 = ''

  _selectedLanguage = ''
  _selectedLanguage2 = ''

  _selectedArticle = ''
  _selectedArticle2 = ''

  _selectedPos = ''
  _selectedPos2 = ''

  _selectedLngHasArticle = false
  _selectedLngHasArticle2 = false

  _wordMaxlength = 100
  _definitionMaxlength = 500
  _exampleMaxlength = 200

  _loadedCats: boolean = false
  _loadedTags: boolean = false
  _loadedDics: boolean = false

  _favLanguages: string[] = []

  _userCats: any[] = []
  _userTags: any[] = []
  _userDics: any[] = []
  _isCreateBtnLoading = false
  _submitBtnText = 'Create'
  _selectedPrivacyChipOptions: string = ''
  _selectedChipOptions: string[] = ['word']

  private destroy$ = new Subject<void>()

  languages: SelectOption[] = []
  articles: SelectOption[] = []
  poses: SelectOption[] = []

  languages2: SelectOption[] = []
  articles2: SelectOption[] = []
  poses2: SelectOption[] = []

  constructor(
    private _fb: FormBuilder,
    private _themeService: ThemeService,
    private _snackBar: MatSnackBar,
    private cat_service: CategoryService,
    private tag_service: TagService,
    private dic_service: DictionaryService,
    private user_info: UserService,
    private _dialogRef: MatDialogRef<DdcAddMobileSheetComponent>,
    createWordPairDicCmd: CreateWordPairDicCommand,
  ) {
    this.CreateWordPairDicCommand = createWordPairDicCmd
    this._form = this._fb.group({
      _type: ['word'],

      _1_language: ['', Validators.required],
      _1_article: [''],
      _1_word: ['', [Validators.required, Validators.maxLength(this._wordMaxlength)]],
      _1_pos: [''],
      _1_definition: ['', [Validators.maxLength(this._definitionMaxlength)]],
      _1_example: ['', [Validators.maxLength(this._exampleMaxlength)]],

      _2_language: ['', Validators.required],
      _2_article: [''],
      _2_word: ['', [Validators.required, Validators.maxLength(this._wordMaxlength)]],
      _2_pos: [''],
      _2_definition: ['', [Validators.maxLength(this._definitionMaxlength)]],
      _2_example: ['', [Validators.maxLength(this._exampleMaxlength)]],

      _privacy: ['P01'],

      _dictionary_title: [
        '',
        [Validators.minLength(1), Validators.maxLength(this._exampleMaxlength)],
      ],
      _dictionary_description: [
        '',
        [Validators.minLength(1), Validators.maxLength(this._exampleMaxlength)],
      ],
    })

    this._themeService.theme$.subscribe(theme => {
      this._isDarkTheme = theme === 'dark-theme'
      this._isLightTheme = theme === 'light-theme'
      this._isPaperTheme = theme === 'paper-theme'
    })
  }

  ngOnInit(): void {
    this._selectedPrivacyChipOptions = 'P01'

    this.getFavLanguages()
    this.getLanguages()
    this.getLanguages2()

    this.getArticles()
    this.getArticles2()

    this.getPos()
    this.getPos2()

    this.getUserTags()
    this.getUserCats()
    this.getUserDics()

    // Initialize form values signal
    this.formValues.set(this._form.value)

    // Subscribe to form value changes
    this._form.valueChanges.subscribe(value => {
      this.formValues.set(value)
      const args = this.commandArgs()
      if (args) {
        this.CreateWordPairDicCommand.setArgs(args)
      }
    })

    Promise.all([this.getLanguages(), this.getArticles(), this.getPos()]).then(() => {
      setTimeout(() => {
        this._isDialogLoading = false
      }, 500)
    })

    //set selectedDics from data if available
    if (this.target_dic() && this.target_dic().id && this.target_dic().title.length > 0) {
      this.selectedDics.set([{ id: this.target_dic().id, label: this.target_dic().title }])
      this._form.patchValue({
        _dictionary_title: this.target_dic().title,
        _dictionary_description: '',
      })
    }

    this.updateSelectedDics(this.selectedDics())
  }
  getFavLanguages(): void {
    this.user_info.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
      if (res && res.username) {
        // Clear existing favorites to avoid duplicates
        this._favLanguages = []

        if (res.language_1) this._favLanguages.push(res.language_1)
        if (res.language_2) this._favLanguages.push(res.language_2)
        if (res.language_3) this._favLanguages.push(res.language_3)

        // Rebuild language lists with favorites at the top
        this.getLanguages()
        this.getLanguages2()
      }
    })
  }

  isFormValid(): boolean {
    if (this._selectedChipOptions.includes('word')) {
      const lng = this._form.get('_1_language').valid
      const art = this._form.get('_1_article').valid
      const word = this._form.get('_1_word').valid
      const pos = this._form.get('_1_pos').valid
      const exp = this._form.get('_1_example').valid
      const def = this._form.get('_1_definition').valid

      if (lng && art && word && pos && exp && def) return true
    } else if (this._selectedChipOptions.includes('pair')) {
      const lng = this._form.get('_1_language').valid
      const art = this._form.get('_1_article').valid
      const word = this._form.get('_1_word').valid
      const pos = this._form.get('_1_pos').valid
      const exp = this._form.get('_1_example').valid
      const def = this._form.get('_1_definition').valid

      const lng2 = this._form.get('_2_language').valid
      const art2 = this._form.get('_2_article').valid
      const word2 = this._form.get('_2_word').valid
      const pos2 = this._form.get('_2_pos').valid
      const exp2 = this._form.get('_2_example').valid
      const def2 = this._form.get('_2_definition').valid
      if (
        lng &&
        art &&
        word &&
        pos &&
        exp &&
        def &&
        lng2 &&
        art2 &&
        word2 &&
        pos2 &&
        exp2 &&
        def2
      ) {
        return true
      }
    } else if (this._selectedChipOptions.includes('dictionary')) {
      const dic_title = this._form.get('_dictionary_title')
      const dic_description = this._form.get('_dictionary_description')
      if (
        dic_title &&
        dic_description &&
        dic_title.valid &&
        dic_description.valid &&
        dic_title.value &&
        dic_description.value &&
        dic_title.value.length > 0 &&
        dic_description.value.length > 0
      )
        return true
    }
    return false
  }

  getLanguages(): void {
    const allLanguages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))

    // Separate favorite and other languages
    const favoriteLanguages = allLanguages.filter(lang =>
      this._favLanguages.includes(lang.raw_value),
    )
    const otherLanguages = allLanguages.filter(lang => !this._favLanguages.includes(lang.raw_value))

    // Combine with separator if there are favorite languages
    this.languages = []
    if (favoriteLanguages.length > 0) {
      this.languages.push(...favoriteLanguages)
      this.languages.push({ raw_value: 'separator', view_value: '───── Other Languages ─────' })
      this.languages.push(...otherLanguages)
    } else {
      this.languages = allLanguages
    }
  }

  getLanguages2(): void {
    const allLanguages = Object.entries(Languages).map(([key, [viewValue, rawValue]]) => ({
      raw_value: rawValue,
      view_value: viewValue,
    }))

    // Separate favorite and other languages
    const favoriteLanguages = allLanguages.filter(lang =>
      this._favLanguages.includes(lang.raw_value),
    )
    const otherLanguages = allLanguages.filter(lang => !this._favLanguages.includes(lang.raw_value))

    // Combine with separator if there are favorite languages
    this.languages2 = []
    if (favoriteLanguages.length > 0) {
      this.languages2.push(...favoriteLanguages)
      this.languages2.push({ raw_value: 'separator', view_value: '───── Other Languages ─────' })
      this.languages2.push(...otherLanguages)
    } else {
      this.languages2 = allLanguages
    }
  }
  getArticles(): void {
    const articlesArray = Object.values(Article) as string[]

    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getUserTags(): void {
    this.tag_service.get_tags().subscribe((res: any) => {
      if (res) {
        this._userTags = res
        this._loadedTags = true
      }
    })
  }

  getUserCats(): void {
    this.cat_service.get_cats().subscribe((res: any) => {
      if (res) {
        this._userCats = res
        this._loadedCats = true
      }
    })
  }

  getUserDics(): void {
    this.dic_service.get_dics(true).subscribe({
      next: (res: any) => {
        if (res) {
          // Map the response to transform 'title' to 'label'
          this._userDics = res.map((dic: { id: number; title: string }) => ({
            id: dic.id,
            label: dic.title,
          }))
          this._loadedDics = true
        } else {
        }
      },
      error: (error: any) => {
        this._isSubscriptionError = true
      },
    })
  }

  getArticles2(): void {
    const articlesArray = Object.values(Article) as string[]

    this.articles2.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of articlesArray) {
      this.articles2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getPos(): void {
    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  getPos2(): void {
    const posArray = Object.values(PartOfSpeech) as string[]

    this.poses2.push({
      raw_value: null,
      view_value: 'None',
    })

    // Loop through the enum values and create SelectOption objects
    for (const value of posArray) {
      this.poses2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  onLanguageChange(event: any): void {
    const selectedLng = event.value

    this.articles = []
    this.articles.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray = ArticlesByLanguage[selectedLng]

    if (articlesArray != undefined) {
      this._selectedLngHasArticle = true
    } else {
      this._selectedLngHasArticle = false
      this._selectedArticle = null
    }

    for (const value of articlesArray) {
      this.articles.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  onLanguageChange2(event: any): void {
    const selectedLng2 = event.value

    this.articles2 = []
    this.articles2.push({
      raw_value: null,
      view_value: 'None',
    })

    const articlesArray2 = ArticlesByLanguage[selectedLng2]

    if (articlesArray2 != undefined) {
      this._selectedLngHasArticle2 = true
    } else {
      this._selectedLngHasArticle2 = false
    }

    for (const value of articlesArray2) {
      this.articles2.push({
        raw_value: value,
        view_value: value,
      })
    }
  }

  updateSelectedTags(tags: any): void {
    this.selectedTags.set(tags)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedTags2(tags: any): void {
    this.selectedTags2.set(tags)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedCat(cat: any): void {
    this.selectedCategory.set(cat)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedCat2(cat: any): void {
    this.selectedCategory2.set(cat)
    this.updateCommandArgsFromSignals()
  }

  updateSelectedDics(dictionaries: any): void {
    this.selectedDics.set(dictionaries)
    this.updateCommandArgsFromSignals()
  }

  updateArgs(): void {
    if (this._form.valid) {
      this.formValues.set(this._form.value)
      const args = this.commandArgs()
      if (args) {
        this.CreateWordPairDicCommand.setArgs(args)
      }
    }
  }

  private updateCommandArgsFromSignals(): void {
    this.CreateWordPairDicCommand.setArgs(this.commandArgs())
  }
  add(): void {
    if (this._form.valid) {
      this.updateArgs()
      this._isCreateBtnLoading = true

      this.CreateWordPairDicCommand.execute().subscribe(success => {
        this._isCreateBtnLoading = false
        if (success) {
          this._wordCreated = true
          this._addedField = this._form.get('_1_word')?.value
        } else {
          this._addError = 'Error creating entry'
          this._snackBar.open(this._addError, 'Close', {
            duration: 3000,
          })
        }
      })
    } else {
      this._snackBar.open('Please fill in all required fields', 'Close', {
        duration: 3000,
      })
    }
  }

  closeDialog(): void {
    this._dialogRef.close()
  }
}
